import { FC, useMemo, useState } from 'react';
import { List } from '@mui/material';
import { HistoryListItem } from '../components/HistoryListItem';
import { FilterBar } from '../components/FilterBar';
import { NoSchedules } from '../components/NoSchedules';
import { useHistory } from '../hooks/useHistory';
import { useNavigate } from 'react-router-dom';
import { Payment, DescriptionCode, DESCRIPTION_CODE_LABEL } from '@lendica/utils';
import { parse, compareAsc, compareDesc, isToday } from 'date-fns';
import { VirtualizedPaymentsList } from '../components/VirtualizedPaymentsList';

interface HistoryProps {
    payments: Payment[];
    isInitialLoading: boolean;
    isFetchingMore: boolean;
    hasNext: boolean;
    loadMore: () => void;
}

const filterer = (filters: any, items: Payment[]) => {
    let filteredItems: Payment[] = items;
    let filterCategories = Object.keys(filters);

    filteredItems = filteredItems.sort((a: Payment, b: Payment) => {
        let date1 = parse(a.event_date, 'yyyy-MM-dd', new Date());
        let date2 = parse(b.event_date, 'yyyy-MM-dd', new Date());
        return compareDesc(date1, date2);
    });

    filterCategories.map(category => {
        if (Object.keys(filters).length === 0) {
            return filteredItems;
        }
        if (filters['earliestFirst']) {
            filteredItems = filteredItems.sort((a: Payment, b: Payment) => {
                let date1 = parse(a.event_date, 'yyyy-MM-dd', new Date());
                let date2 = parse(b.event_date, 'yyyy-MM-dd', new Date());
                return compareAsc(date1, date2);
            });
        }
        if (category == 'status') {
            if (filters[category].length === 0) {
                return filteredItems;
            } else {
                filteredItems = filteredItems.filter(x =>
                    filters[category].includes(x.resulting_status)
                );
            }
        }
        if (category === 'dealId') {
            if (filters[category].length === 0) {
                return filteredItems;
            } else {
                filteredItems = filteredItems.filter(x => filters[category].includes(x.deal));
            }
        }
    });

    return filteredItems;
};

export const History: FC<HistoryProps> = ({
    payments,
    isInitialLoading,
    hasNext,
    loadMore,
    isFetchingMore,
}) => {
    const [filters, setFilters] = useState({});
    const navigate = useNavigate();

    // history = strictly in the past
    const now = useMemo(() => new Date(), []);
    const source = useMemo(
        () =>
            payments.filter(p => {
                const d = parse(p.event_date, 'yyyy-MM-dd', new Date());
                return compareDesc(d, now) > 0;
            }),
        [now, payments]
    );

    const { filtersList } = useHistory({ payments: source });

    const onChange = (value: any) => {
        setFilters(value);
    };

    const handleNavigation = (id: string) => {
        const payment = payments.find(payment => payment.id === id)!;
        navigate(`detail/${id}`, { state: { payment } });
    };

    const filteredResults = useMemo(() => filterer(filters, source), [filters, source]);

    return (
        <>
            {isInitialLoading ? null : source.length !== 0 ? (
                filteredResults.length === 0 ? (
                    <>
                        <FilterBar
                            filters={filtersList}
                            initialValue={filters}
                            onChange={onChange}
                        />
                        <NoSchedules subtitle="No results matching filters." />
                    </>
                ) : (
                    <>
                        <FilterBar
                            filters={filtersList}
                            initialValue={filters}
                            onChange={onChange}
                        />
                        <VirtualizedPaymentsList
                            items={filteredResults}
                            variant="history"
                            loadMore={loadMore}
                            hasNext={hasNext}
                            isFetchingMore={isFetchingMore}
                            onHistoryRowClick={id => handleNavigation(id)}
                        />
                    </>
                )
            ) : (
                <NoSchedules subtitle="You have no payment history." />
            )}
        </>
    );
};
